import {
    FormControl,
    FormControlLabel
} from '@mui/material'
import { ButtonForm } from 'Components/Form/styles'
import { Column, Row } from 'Components/Grid'
import { Button, Checkbox, Select, TextField } from 'Components/ui'
import { MuiChipsInput } from 'mui-chips-input'
import { Controller, useWatch } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useCountryConfig } from 'hooks/useCountryConfig'
import { validateTaxId, stripTaxIdFormatting } from 'utils/tax_id_validators'

export const Detalhes = ({
    handleNext,
    register,
    control,
    handleSubmit,
    errors,
    getValues
}) => {
    const navigate = useNavigate()
    const { t } = useTranslation()
    const countryValue = useWatch({ control, name: 'country' })
    const { taxIdLabel, taxIdMask, taxIdPlaceholder, countryConfig, allConfigs, isError: isCountryConfigError, isLoading: isCountryConfigLoading } = useCountryConfig(countryValue)
    const onSubmit = () => {
        handleNext()
    }

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Row justifyContent="center">
                <Column xs={12} className="d-flex">
                    <TextField
                        {...register('name', { required: 'Nome é obrigatório' })}
                        label="Nome"
                        autoFocus
                        error={errors?.name ? true : false}
                        fullWidth
                        sx={{ margin: '0 auto 24px' }}
                        helperText={errors?.name ? errors.name.message : null}
                        InputLabelProps={{ shrink: true }}
                    />
                </Column>
            </Row>
            <Row justifyContent="center">
                <Column xs={12} className="d-flex">
                    <TextField
                        {...register('corporateName', { required: 'Razão social é obrigatório' })}
                        label="Razão social"
                        autoFocus
                        error={errors?.corporateName ? true : false}
                        fullWidth
                        sx={{ margin: '0 auto 24px' }}
                        helperText={errors?.corporateName ? errors.corporateName.message : null}
                        InputLabelProps={{ shrink: true }}
                    />
                </Column>
            </Row>
            <Row justifyContent="center">
                <Column xs={12} className="d-flex">
                    <TextField
                        {...register('slug', { required: 'Identificador é obrigatório' })}
                        label="Identificador"
                        error={errors?.slug ? true : false}
                        fullWidth
                        sx={{ margin: '0 auto 24px' }}
                        helperText={errors?.slug ? errors.slug.message : null}
                        InputLabelProps={{ shrink: true }}
                    />
                </Column>
            </Row>
            <Row justifyContent="center">
                <Column xs={12} className="d-flex">
                    <Controller
                        control={control}
                        name="country"
                        rules={{ required: 'País é obrigatório' }}
                        render={({ field: { onChange, value } }) => (
                            <Select
                                name="country"
                                label="País"
                                value={value}
                                onChange={onChange}
                                disabled={isCountryConfigError}
                                error={!!errors?.country || isCountryConfigError}
                                helperText={isCountryConfigError
                                    ? 'Não foi possível carregar os países. Recarregue a página.'
                                    : (errors?.country ? errors.country.message : '')}
                                sx={{ marginBottom: '24px' }}
                                options={[
                                    { value: '', label: isCountryConfigError ? 'Erro ao carregar países' : 'Selecione um País' },
                                    ...allConfigs.map(cc => ({ value: cc.countryCode, label: cc.name.pt })),
                                ]}
                            />
                        )}
                    />
                </Column>
            </Row>
            <Row justifyContent="center">
                <Column xs={12} className="d-flex">
                    <Controller
                        control={control}
                        name="defaultLanguage"
                        render={({ field: { onChange, value } }) => (
                            <Select
                                name="defaultLanguage"
                                value={value}
                                onChange={onChange}
                                error={!!errors?.defaultLanguage}
                                helperText={errors?.defaultLanguage ? errors.defaultLanguage.message : ''}
                                sx={{ marginBottom: '24px' }}
                                options={[
                                    { value: '', label: 'Selecione um idioma' },
                                    { value: 'pt', label: 'Português' },
                                    { value: 'en', label: 'English' },
                                    { value: 'es', label: 'Español' },
                                ]}
                            />
                        )}
                    />
                </Column>
            </Row>
            <Row justifyContent="center">
                <Column xs={12} className="d-flex">
                    <TextField
                        {...register('cnpj', {
                            required: `${taxIdLabel || 'Tax ID'} é obrigatório`,
                            validate: (value) => {
                                if (!value || !countryValue) return true;
                                if (isCountryConfigLoading) return 'Aguardando configuração de país...';
                                if (!countryConfig) return true;
                                const stripped = stripTaxIdFormatting(value);
                                // Only run format regex when value has visual separators (formatted input)
                                // Skip for already-stripped DB values on edit to avoid false failures
                                if (stripped !== value && countryConfig.taxId?.regex) {
                                    const formatOk = new RegExp(countryConfig.taxId.regex).test(value);
                                    if (!formatOk) return `Formato inválido. Use: ${taxIdMask}`;
                                }
                                const checkOk = validateTaxId(stripped, countryValue);
                                if (!checkOk) return `${taxIdLabel} inválido. Use o formato: ${taxIdMask}`;
                                return true;
                            },
                        })}
                        label={taxIdLabel || 'CNPJ'}
                        placeholder={taxIdPlaceholder}
                        error={errors?.cnpj ? true : false}
                        fullWidth
                        sx={{ margin: '0 auto 24px' }}
                        helperText={errors?.cnpj ? errors.cnpj.message : null}
                        InputLabelProps={{ shrink: true }}
                    />
                </Column>
            </Row>
            <Row justifyContent="center">
                <Column xs={12} className="d-flex">
                    <TextField
                        {...register('size')}
                        label="Quantidade de funcionários"
                        fullWidth
                        sx={{ margin: '0 auto 24px' }}
                        InputLabelProps={{ shrink: true }}
                    />
                </Column>
            </Row>
            <Row justifyContent="center">
                <Column xs={12} className="d-flex">
                    <TextField
                        {...register('income')}
                        label="Receita"
                        fullWidth
                        sx={{ margin: '0 auto 24px' }}
                        InputLabelProps={{ shrink: true }}
                    />
                </Column>
            </Row>
            <Row justifyContent="center">
                <Column xs={12} className="d-flex">
                    <TextField
                        {...register('industry')}
                        label="Indústria"
                        fullWidth
                        sx={{ margin: '0 auto 24px' }}
                        InputLabelProps={{ shrink: true }}
                    />
                </Column>
            </Row>
            <Row justifyContent="center">
                <Column xs={12} className="d-flex">
                    <FormControl
                        fullWidth
                        sx={{ margin: '0 auto 24px' }}
                    >
                        <Controller
                            control={control}
                            name="domains"
                            render={({ field: { onChange, value }}) => (
                                <MuiChipsInput
                                label="Domínios" 
                                value={value} 
                                onChange={onChange}
                                InputLabelProps={{ shrink: true }}
                                placeholder="Digite e aperte enter"
                                renderChip={(Component, props) => {
                                    return (
                                    <Component 
                                        {...props}
                                        variant="outlined"
                                        color="primary"
                                        sx={{
                                        width: 'fit-content',
                                        }}
                                    />
                                    )
                                }}
                                sx={{
                                    '& .MuiButtonBase-root.MuiChip-root.MuiChip-filled': {
                                    width: 'fit-content'
                                    },
                                    '& .MuiButtonBase-root.MuiIconButton-root': {
                                    width: 'fit-content',
                                    marginTop: '0px'
                                    }
                                }}/>
                            )}
                        ></Controller>
                    </FormControl>
                </Column>
            </Row>
            <Row>
                <Column xs={12} sm={6} md={6} className="d-flex">
                    <Controller
                        control={control}
                        name="active"
                        render={({ field: { onChange, value }}) => (
                            <FormControlLabel
                                value={value}
                                control={<Checkbox checked={value} onChange={onChange} name="active" />}
                                label="Ativo"
                                sx={{marginLeft: 0, marginBottom: '16px'}}
                            />
                        )}
                    />
                </Column>
                <Column xs={12} sm={6} md={6} className="d-flex">
                    <Controller
                        control={control}
                        name="partner"
                        render={({ field: { onChange, value }}) => (
                            <FormControlLabel
                                value={value}
                                control={<Checkbox checked={value} onChange={onChange} name="partner" />}
                                label="Parceiro"
                                sx={{marginLeft: 0, marginBottom: '16px'}}
                            />
                        )}
                    />
                </Column>
            </Row>
            <Row>
                <Column xs={12} className="d-flex">
                    <Controller
                        control={control}
                        rules={{
                        validate: () => {
                            return getValues('signedPlan') !== ''
                        },
                        required: 'Plano de assinatua é obrigatório',
                        }}
                        name="signedPlan"
                        render={({ field: { onChange, value } }) => (
                        <Select
                            name="signedPlan"
                            value={value}
                            onChange={onChange}
                            error={errors?.signedPlan ? true : false}
                            helperText={errors?.signedPlan ? errors.signedPlan.message : ''}
                            sx={{ marginBottom: '24px' }}
                            options={[
                                { value: '', label: 'Selecione um Plano de assinatura' },
                                ...['Free', 'Starter', 'Premium'].map((item) => ({ value: item, label: item })),
                            ]}
                        />
                        )}
                    />
                </Column>
            </Row>
            <Row>
                <Column>
                    <TextField
                    {...register('description')}
                    label="Descrição"
                    multiline
                    rows={4}
                    sx={{ margin: '0 auto 24px', width: '100%' }}
                    InputLabelProps={{ shrink: true }}
                    />
                </Column>
            </Row>
            <Row justifyContent="end" alignItems="center">
                <Column xs={4} md={3} className="d-flex" style={{ gap: '8px', justifyContent: 'flex-end' }}>
                <Button
                    variant="outline"
                    id="cancel"
                    onClick={() => navigate('/companies-partner')}
                    sx={{ height: '44px', padding: '0 20px' }}
                >
                    {t('common:buttons.cancel')}
                </Button>
                <ButtonForm type="submit">Salvar</ButtonForm>
                </Column>
            </Row>  
        </form>
    )
}